import { z } from 'zod';

/**
 * Protective-action link model (R2 / #478 incident-as-aggregator).
 *
 * DOCTRINE: an evacuation is a PROTECTIVE ACTION / measure (Ontario IMS — a step to
 * protect people, distinct from the incident itself). R2 makes an evac order / life-
 * safety CAP a protective-action ATTRIBUTE of its incident instead of a competing
 * peer alert: it AUTO-links (a read-time projection — exactly one OPEN incident within
 * ~25 km) or is MANUALLY linked via the "Link to incident" dropdown, and is always
 * reversibly unlinkable. SCOPE FENCE: this binds evac + related CAP ONLY — a raw
 * hotspot/detection → incident stays R1 promote/confirm or R5 complex-membership.
 */

/** The three evac/CAP surfaces that can be a protective-action attribute of an incident. */
export const PROTECTIVE_ACTION_KINDS = [
  'cap_alert', // a life-safety / evacuation CAP (cap_alerts)
  'arcgis_feature', // the production GeoBC evac feed (arcgis_public_features, category='evacuation')
  'operator_evac_order', // an operator-declared evacuation order (operator_evacuation_orders)
] as const;
export const ProtectiveActionKindSchema = z.enum(PROTECTIVE_ACTION_KINDS);
export type ProtectiveActionKind = z.infer<typeof ProtectiveActionKindSchema>;

/** Link provenance on the wire: 'manual' = operator persisted it; 'auto' = the
 *  read-time projection. (The DB 'detached' marker never surfaces as a link.) */
export const ProtectiveActionLinkModeSchema = z.enum(['manual', 'auto']);
export type ProtectiveActionLinkMode = z.infer<typeof ProtectiveActionLinkModeSchema>;

/**
 * One protective action collapsed INTO an incident (rendered as a line under the
 * incident + its zone as the incident's overlay — NOT a competing queue row).
 */
export const ProtectiveActionSchema = z.object({
  kind: ProtectiveActionKindSchema,
  id: z.string(),
  /** Short human label, e.g. "Tornado Warning — Kenora" / "Kenora 45 — Order". */
  label: z.string(),
  /** Register: 'order' | 'alert' for evac surfaces; the CAP severity for a CAP; null. */
  level: z.string().nullable(),
  /** The issuing authority for an operator evac order (the COP RECORDS on behalf of —
   *  rendered "Declared by <authority>"). Null for CAP / GeoBC (their attribution is
   *  the agency string). */
  issuing_authority: z.string().nullable(),
  /** #458 — the coordinating authority (Far-North tripartite case), nullable. Rendered
   *  "Coordinated by <name>" as a SECONDARY line AFTER the issuer, never above it. Null
   *  for CAP / GeoBC and for orders with no coordinator. */
  coordinating_authority: z.string().nullable(),
  /** How the link was established: 'manual' (operator dropdown) | 'auto' (projection). */
  link_mode: ProtectiveActionLinkModeSchema,
  lon: z.number(),
  lat: z.number(),
});
export type ProtectiveAction = z.infer<typeof ProtectiveActionSchema>;

/**
 * A community the incident's protective-action zone(s) cover (the Municipal EOC's
 * "my community is in-zone" coverage-read): ST_Intersects(zone, service_area) OR
 * ST_Contains(zone, centroid).
 */
export const InZoneCommunitySchema = z.object({
  id: z.string(),
  name: z.string(),
  community_type: z.string(),
  lon: z.number(),
  lat: z.number(),
  /** Which coverage predicate matched: 'service_area' (polygon ∩) or 'point' (centroid ∈). */
  coverage: z.enum(['service_area', 'point']),
});
export type InZoneCommunity = z.infer<typeof InZoneCommunitySchema>;

/**
 * A candidate incident for the manual "Link to incident" dropdown — OPEN incidents
 * plus recently-deactivated ones (so a flare-up can REIGNITE a deactivated incident),
 * sorted nearest-first. `within_auto_radius` flags the ones the auto-link would have
 * picked; `is_deactivated` flags the reignition candidates.
 */
export const LinkableIncidentSchema = z.object({
  id: z.string(),
  incident_number: z.string(),
  name: z.string(),
  hazard_type: z.string(),
  status: z.string(),
  lifecycle_state: z.string(),
  lon: z.number(),
  lat: z.number(),
  distance_km: z.number(),
  within_auto_radius: z.boolean(),
  is_deactivated: z.boolean(),
});
export type LinkableIncident = z.infer<typeof LinkableIncidentSchema>;

export const LinkableIncidentsResponseSchema = z.object({
  incidents: z.array(LinkableIncidentSchema),
});
export type LinkableIncidentsResponse = z.infer<typeof LinkableIncidentsResponseSchema>;

/* ------------------------------------------------------------------ *
 * Mutations
 * ------------------------------------------------------------------ */

/** POST /api/protective-actions/link — MANUAL link (dropdown). Linking to a
 *  deactivated incident REIGNITES it (advance deactivated→confirmed). */
export const LinkAlertToIncidentSchema = z.object({
  kind: ProtectiveActionKindSchema,
  alert_id: z.string().uuid(),
  incident_id: z.string().uuid(),
  session_id: z.string().min(1).max(128),
});
export type LinkAlertToIncident = z.infer<typeof LinkAlertToIncidentSchema>;

/** POST /api/protective-actions/unlink — always-reversible detach (suppresses
 *  re-auto-link via incident_link_mode='detached'). */
export const UnlinkAlertFromIncidentSchema = z.object({
  kind: ProtectiveActionKindSchema,
  alert_id: z.string().uuid(),
  session_id: z.string().min(1).max(128),
});
export type UnlinkAlertFromIncident = z.infer<typeof UnlinkAlertFromIncidentSchema>;

/** The link/unlink response. `reignited` true when a deactivated incident was reopened. */
export const ProtectiveActionLinkResponseSchema = z.object({
  linked: z.boolean(),
  unlinked: z.boolean().optional(),
  reignited: z.boolean().optional(),
  incident_id: z.string().nullable(),
});
export type ProtectiveActionLinkResponse = z.infer<typeof ProtectiveActionLinkResponseSchema>;
